import React, { useState } from "react"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { PencilIcon } from "@heroicons/react/24/outline"

const ClassNameEditor = ({
  className = "",
  isEditing = false,
  isCreatingNew = false,
  existingClasses = [],
  onEdit,
  onSave,
  onCancel
}) => {
  const [localName, setLocalName] = useState(className)
  const [error, setError] = useState("")

  // Validate class name
  const validateClassName = (name) => {
    const trimmedName = name?.trim()

    if (!trimmedName) {
      return "Class name is required"
    }

    // Check for invalid characters
    if (!/^[a-zA-Z_][a-zA-Z0-9_-]*$/.test(trimmedName)) {
      return "Class name can only contain letters, numbers, hyphens, and underscores. Must start with a letter or underscore."
    }

    // Check if class already exists (only when creating new or renaming)
    if (isCreatingNew || trimmedName !== className) {
      const exists = existingClasses.some(cls => cls.name === trimmedName)
      if (exists) {
        return "A class with this name already exists"
      }
    }

    return ""
  }

  const handleNameChange = (value) => {
    setLocalName(value)
    const validationError = validateClassName(value)
    setError(validationError)
  }

  const handleSave = () => {
    const trimmedName = localName?.trim()
    const validationError = validateClassName(trimmedName)

    if (validationError) {
      setError(validationError)
      return
    }

    setError("")
    onSave?.(trimmedName)
    setLocalName(trimmedName)
  }

  const handleCancel = () => {
    setLocalName(className)
    setError("")
    onCancel?.()
  }

  return (
    <div>
      <Typography variant="admin_body_small" className="font-medium text-admin_text mb-3">
        Class Name
      </Typography>

      {isEditing ? (
        <div>
          <div className="flex items-center gap-2">
            <span className="text-admin_text_secondary">.</span>
            <Input
              variant="admin_compact"
              value={localName}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Enter class name"
              className={`flex-1 ${error ? 'border-admin_error' : ''}`}
              autoFocus={true}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSave()
                } else if (e.key === 'Escape') {
                  handleCancel()
                }
              }}
            />
            <Button
              variant="admin_secondary"
              onClick={handleSave}
              className="px-3 py-1 text-xs"
              disabled={!!error}
            >
              Save
            </Button>
            <Button
              variant="admin_secondary"
              onClick={handleCancel}
              className="px-3 py-1 text-xs"
            >
              Cancel
            </Button>
          </div>
          {error && (
            <div className="mt-2 text-xs text-admin_error">
              {error}
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-admin_text_secondary">.</span>
          <span className="font-mono text-sm bg-admin_neutral_1 px-2 py-1 rounded flex-1">
            {className}
          </span>
          {!isCreatingNew && (
            <Button
              variant="admin_secondary"
              onClick={onEdit}
              className="px-2 py-1"
              title="Edit class name"
            >
              <PencilIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default ClassNameEditor